import { useEffect, useRef, useState, useCallback } from "react";

interface ConnectParams {
    storeId: string;
    agentId: string;
    requestId: string;
    token: string;
    onConnect: () => void;
}

export const useAgentChatSocket = () => {
    const wsRef = useRef<WebSocket | null>(null);
    const [messages, setMessages] = useState<string[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const messageQueueRef = useRef<string[]>([]); // Queue for messages sent before connection
    const connectParamsRef = useRef<ConnectParams | null>(null); // Store connection params for reconnection

    // Connect WebSocket
    const connectWebSocket = useCallback(({ storeId, agentId, requestId, token, onConnect }: ConnectParams) => {
        if (!storeId || !agentId || !requestId || !token) {
            console.warn("Missing WebSocket connection parameters.");
            return;
        }

        // Store params for potential reconnection
        connectParamsRef.current = { storeId, agentId, requestId, token, onConnect };

        const wsUrl = `wss://agentchatstage.goldeninfosystems.com/ws/agent-chat/${storeId}/${agentId}?chat_request=${requestId}`;
        console.log("Connecting to WebSocket:", wsUrl);

        const socket = new WebSocket(wsUrl, token);
        wsRef.current = socket;

        socket.onopen = () => {
            console.log("✅ WebSocket connected!");
            setIsConnected(true);
            onConnect();

            // Flush queued messages
            messageQueueRef.current.forEach((msg) => {
                socket.send(JSON.stringify({ type: "MESSAGE", content: msg }));
            });
            messageQueueRef.current = [];
        };

        socket.onmessage = (event) => {
            console.log("📩 Message received:", event.data);
            setMessages((prev) => [...prev, event.data]);
        };

        socket.onerror = (error) => {
            console.error("❌ WebSocket error:", error);
        };

        socket.onclose = (event) => {
            console.warn("WebSocket closed unexpectedly.");
            console.warn("Code:", event.code); // e.g., 1006
            console.warn("Reason:", event.reason); // Usually empty for 1006
            console.warn("WasClean:", event.wasClean);
            setIsConnected(false);
        };
    }, []);

    // Send Message with null handling and reconnection attempt
    const sendMessage = useCallback((message: string) => {
        if (!message) return;

        const socket = wsRef.current;
        console.log("wsRef.current in sendMessage:", socket); // Debug log

        if (!socket) {
            console.warn("⚠️ WebSocket is null. Queuing message:", message);
            messageQueueRef.current.push(message);

            // Attempt to reconnect if params are available
            if (connectParamsRef.current) {
                console.log("Attempting to reconnect WebSocket...");
                connectWebSocket(connectParamsRef.current);
            }
            return;
        }

        if (socket.readyState === WebSocket.OPEN) {
            console.log("📤 Sending message:", message);
            socket.send(message);
        } else {
            console.warn("🕒 WebSocket not ready. Queuing message:", message, socket);
            messageQueueRef.current.push(message);
        }
    }, [connectWebSocket]); // Add connectWebSocket as dependency

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (wsRef.current) {
                console.log("🧹 Cleaning up WebSocket connection, wsRef.current:", wsRef.current);
                wsRef.current.close();
                wsRef.current = null; // Explicitly reset to null after cleanup
            }
        };
    }, []);

    return {
        connectWebSocket,
        sendMessage,
        messages,
        isConnected,
    };
};